import {
  FeatureFlag,
  FeatureFlags,
  FeatureName,
  FeatureScopes,
  isWildcard,
} from 'src/services/feature-flags';

export type FeatureFlagScope = {
  unitId?: number;
  regionId?: number;
};

/**
 * Answers the region question: may the surface offer this region?
 *
 * A wildcard means the flag does not narrow regions at all, so the ACL alone
 * decides. An enumerated list is authoritative — a region absent from it has
 * been deliberately withheld, because nothing in it is live on the Portal yet.
 */
function allowsRegion(flag: FeatureFlag, regionId: number): boolean {
  return isWildcard(flag.regions) || flag.regions.includes(regionId);
}

/**
 * Answers the unit question: may the surface offer this unit?
 *
 * An enumerated unit list is authoritative and needs no further context — it is
 * the live subset of the units the user holds office in. A wildcard means the
 * flag does not enumerate units at all, which the API does deliberately for
 * administrators so that the ACL remains the single filter for each item; in
 * that case the unit is admissible only where its region is. Where regions are
 * unrestricted too, the unit passes outright.
 *
 * Where regions are enumerated but the caller supplied no `regionId`, there is
 * nothing to check the unit against and the answer is no. Failing closed here
 * is deliberate: the alternative hands a Provincial administrator every unit in
 * the country, which is the leak this scoping exists to prevent.
 */
function allowsUnit(
  flag: FeatureFlag,
  unitId: number,
  regionId: number | undefined
): boolean {
  if (!isWildcard(flag.units)) return flag.units.includes(unitId);
  if (isWildcard(flag.regions)) return true;
  if (regionId === undefined) return false;

  return flag.regions.includes(regionId);
}

/**
 * Whether a feature is enabled for one specific region or unit.
 *
 * The subject of the question is the narrowest scope named: a unit if one was
 * given, otherwise a region. A `regionId` passed alongside a `unitId` is
 * context for resolving a wildcard unit list, not a second condition — so a
 * unit named on the flag's own list stays visible whatever region accompanies
 * it.
 *
 * This governs what the interface offers, never what the user may do. Every
 * request is still authorised against the ACL on its own.
 */
export function isEnabled(
  featureName: FeatureName,
  featureFlags: FeatureFlags,
  scope: FeatureFlagScope
): boolean {
  const { regionId, unitId } = scope;
  const flag = featureFlags[featureName];

  if (!flag) return false;

  if (unitId !== undefined) return allowsUnit(flag, unitId, regionId);
  if (regionId !== undefined) return allowsRegion(flag, regionId);

  return false;
}

/**
 * Whether the user holds the feature at all, at any scope. This is the question
 * the left-hand navigation and the route guard ask — the API omits the flag
 * entirely for a user it refuses, so a present flag with anything in either
 * list is a grant.
 */
export function isEnabledAnywhere(
  featureName: FeatureName,
  featureFlags: FeatureFlags
): boolean {
  const flag = featureFlags[featureName];
  if (!flag) return false;
  return flag.units.length > 0 || flag.regions.length > 0;
}

/**
 * Narrows a list of ACL-permitted ids by the flag's list for the same axis.
 *
 * This is the one place the two mechanisms meet: the ACL says what the user may
 * administer, the flag says how much of that is live on the Portal, and the
 * interface may only offer the intersection. A wildcard leaves the ACL list
 * untouched, because the flag is not narrowing that axis. A missing flag scope
 * leaves nothing visible.
 */
export function narrowIdsByFlagScope(
  allowedIds: number[],
  flagScope: FeatureScopes | undefined
): number[] {
  if (!flagScope) return [];
  if (isWildcard(flagScope)) return allowedIds;

  const liveIds = new Set<number>(flagScope);

  return allowedIds.filter((allowedId) => liveIds.has(allowedId));
}
